import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Receipt, Loader2, CheckCircle, Clock, AlertTriangle, 
  CreditCard
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-600', icon: Clock },
  partial: { label: 'Partial', color: 'bg-amber-600', icon: AlertTriangle },
  paid: { label: 'Paid', color: 'bg-green-600', icon: CheckCircle },
};

export default function PublicStatementPage() {
  const { statementId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    loadStatement();
  }, [statementId]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const res = await api.getPublicStatement(statementId);
      setStatement(res.data.statement);
      setProfile(res.data.profile);
    } catch (err) {
      console.error('Failed to load statement:', err);
      setError('Statement not found');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await api.getStatementPaymentStatus(statementId, sessionId);
      if (res.data.status === 'paid') {
        setPaymentSuccess(true);
        loadStatement();
      }
    } catch (err) {
      console.error('Failed to check payment status:', err);
    }
  };

  const handlePayNow = async () => {
    setProcessingPayment(true);
    try {
      const res = await api.createStatementCheckout(statementId);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Failed to create checkout:', err);
      alert(err.response?.data?.detail || 'Unable to process payment');
      setProcessingPayment(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-400" />
          <h1 className="text-xl font-semibold mb-2">Statement Not Found</h1>
          <p className="text-slate-500">This statement may have been deleted or the link is invalid.</p>
        </div>
      </div>
    );
  }

  const status = statusConfig[statement?.status] || statusConfig.open;
  const StatusIcon = status.icon;
  const invoiceSettings = profile?.invoice_settings || {};
  
  // Branding
  const brandingName = invoiceSettings?.company_name || profile?.company_name || profile?.display_name || 'Process Server';
  const brandingTagline = invoiceSettings?.tagline || '';
  const brandingLogo = invoiceSettings?.logo_url || null;
  const logoSize = invoiceSettings?.logo_size || 'medium';
  const footerPhone = invoiceSettings?.footer_phone || profile?.phone || '';
  const footerEmail = invoiceSettings?.footer_email || profile?.email || '';
  const footerWebsite = invoiceSettings?.footer_website || profile?.website || '';
  const footerText = invoiceSettings?.footer_text || '';

  // Theme settings
  const theme = invoiceSettings?.theme || {};
  const primaryColor = theme.primary_color || '#1e40af';
  const accentColor = theme.accent_color || '#3b82f6';
  const headerBgColor = theme.header_bg_color || '#f8fafc';
  const textColor = theme.text_color || '#1e293b';
  const fontFamily = theme.font_family || 'system';

  const fontFamilyMap = {
    'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'inter': '"Inter", sans-serif',
    'roboto': '"Roboto", sans-serif',
    'open-sans': '"Open Sans", sans-serif',
    'lato': '"Lato", sans-serif',
    'poppins': '"Poppins", sans-serif',
    'playfair': '"Playfair Display", serif',
    'merriweather': '"Merriweather", serif',
    'georgia': 'Georgia, serif',
    'times': '"Times New Roman", Times, serif',
  };
  const fontFamilyStyle = fontFamilyMap[fontFamily] || fontFamilyMap['system'];

  const logoSizeClasses = {
    small: 'h-16 w-auto max-w-[120px]',
    medium: 'h-24 w-auto max-w-[200px]',
    large: 'h-32 w-auto max-w-[300px]',
    xlarge: 'h-48 w-auto max-w-[500px]'
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Payment Success Banner */}
        {paymentSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">Payment Successful!</p>
              <p className="text-sm text-green-600">Thank you for your payment. Your invoices have been updated.</p>
            </div>
          </div>
        )}

        {/* Statement Card */}
        <div 
          className="bg-white rounded-lg shadow-lg overflow-hidden"
          style={{ fontFamily: fontFamilyStyle, color: textColor }}
        >
          {/* Header */}
          <div className="px-5 md:px-8 pt-6 pb-4">
            <div 
              className="rounded-lg py-6 text-center"
              style={{ backgroundColor: headerBgColor }}
            >
              {brandingLogo ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={brandingLogo} 
                    alt="Logo" 
                    className={`${logoSizeClasses[logoSize] || logoSizeClasses.medium} object-contain`}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {brandingTagline && <p className="text-sm mt-2" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                </div>
              ) : (
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ color: primaryColor }}>{brandingName}</h1>
                  {brandingTagline && <p className="text-sm mt-1" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                </div>
              )}
            </div>
          </div>
          
          {/* Meta Bar */}
          <div className="px-5 md:px-8 py-2">
            <div 
              className="rounded-lg border py-3"
              style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
            >
              <div className="flex justify-between items-center text-center">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Statement #</p>
                  <p className="font-mono font-semibold text-sm">{statement.statement_number}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Date</p>
                  <p className="font-semibold text-sm">{formatDate(statement.statement_date)}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Invoices</p>
                  <p className="font-semibold text-sm">{statement.invoice_summaries?.length || 0}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Balance Due</p>
                  <p className="font-semibold text-sm" style={{ color: statement.total_balance_due > 0 ? primaryColor : '#16a34a' }}>
                    {statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 md:px-8 py-6">
            {/* Bill To */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider font-medium mb-2" style={{ color: textColor, opacity: 0.5 }}>Bill To</p>
              <div>
                <p className="font-semibold text-base">{statement.client?.name}</p>
                {statement.client?.email && (
                  <p className="text-sm" style={{ color: accentColor }}>{statement.client.email}</p>
                )}
              </div>
            </div>

            {/* Invoices Header */}
            <div className="border-b-2 pb-2 mb-4" style={{ borderColor: primaryColor }}>
              <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>Invoices</p>
            </div>

            {/* Invoices Table - Desktop */}
            <div className="hidden md:block mb-6">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase">
                    <th className="text-left py-2 font-medium">Invoice</th>
                    <th className="text-center py-2 font-medium w-24">Date</th>
                    <th className="text-center py-2 font-medium w-24">Due Date</th>
                    <th className="text-right py-2 font-medium w-24">Total</th>
                    <th className="text-right py-2 font-medium w-24">Paid</th>
                    <th className="text-right py-2 font-medium w-24">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statement.invoice_summaries?.map((inv) => (
                    <tr key={inv.invoice_id} className={inv.balance_due <= 0 ? 'bg-green-50/50' : ''}>
                      <td className="py-3">
                        <a 
                          href={`/invoice/${inv.invoice_id}`}
                          className="font-medium hover:underline"
                          style={{ color: accentColor }}
                        >
                          {inv.invoice_number}
                        </a>
                      </td>
                      <td className="py-3 text-center text-slate-600 text-sm">{formatDateShort(inv.invoice_date)}</td>
                      <td className="py-3 text-center text-slate-600 text-sm">{formatDateShort(inv.due_date)}</td>
                      <td className="py-3 text-right text-slate-800 text-sm">{formatCurrency(inv.total)}</td>
                      <td className="py-3 text-right text-slate-600 text-sm">{formatCurrency(inv.total_paid)}</td>
                      <td className="py-3 text-right font-semibold text-sm">
                        <span className={inv.balance_due <= 0 ? 'text-green-600' : 'text-slate-800'}>
                          {inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Invoices - Mobile Cards */}
            <div className="md:hidden space-y-3 mb-6">
              {statement.invoice_summaries?.map((inv) => (
                <div key={inv.invoice_id} className={`rounded-lg p-4 ${inv.balance_due <= 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <a 
                      href={`/invoice/${inv.invoice_id}`}
                      className="font-medium hover:underline"
                      style={{ color: accentColor }}
                    >
                      {inv.invoice_number}
                    </a>
                    <span className={`font-semibold ${inv.balance_due <= 0 ? 'text-green-600' : 'text-slate-800'}`}>
                      {inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Date: {formatDateShort(inv.invoice_date)}</span>
                    <span>Total: {formatCurrency(inv.total)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-slate-200 pt-4 mb-6">
              <div className="flex justify-end">
                <div className="w-full md:w-64">
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-slate-500">Total Invoiced</span>
                    <span className="text-slate-700">{formatCurrency(statement.total_invoiced)}</span>
                  </div>
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-slate-500">Total Paid</span>
                    <span className="text-slate-700">{formatCurrency(statement.total_paid)}</span>
                  </div>
                  {statement.statement_paid > 0 && (
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-slate-500">Statement Payments</span>
                      <span className="text-green-600">{formatCurrency(statement.statement_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 mt-2 border-t-2 border-slate-800">
                    <span className="font-bold text-base text-slate-800">Balance Due</span>
                    <span className="font-bold text-lg" style={{ color: statement.total_balance_due > 0 ? primaryColor : '#16a34a' }}>
                      {statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Button */}
            {statement.status !== 'paid' && statement.total_balance_due > 0 && (
              <div className="flex justify-center mb-6">
                <Button
                  size="lg"
                  onClick={handlePayNow}
                  disabled={processingPayment}
                  className="bg-green-600 hover:bg-green-700 px-8"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay {formatCurrency(statement.total_balance_due)}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Notes */}
            {statement.notes && (
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Notes</p>
                <p className="text-slate-600 whitespace-pre-wrap text-sm">{statement.notes}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-5 md:px-8 py-4 border-t border-slate-200">
            {(footerPhone || footerEmail || footerWebsite) && (
              <div className="flex justify-center items-center gap-6 text-xs text-slate-500 mb-2">
                {footerPhone && <span>{footerPhone}</span>}
                {footerEmail && <span>{footerEmail}</span>}
                {footerWebsite && <span>{footerWebsite}</span>}
              </div>
            )}
            <p className="text-xs text-slate-400 text-center">
              {footerText || 'Thank you for your business'}
            </p>
          </div>
        </div>

        {/* Powered by */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Powered by Servio
        </p>
      </div>
    </div>
  );
}
